'use client';
import React from 'react';

import { useImage } from '@hooks/useImage';
import { Shift } from 'types';

import { Modal } from '@components/Modal';
import { ProfileInfoCard } from '@components/Profile';
import { SchoolShiftInfoCard } from '@components/ShiftInfoCards';

import { ShiftFilters, ShiftReactionStatus } from '@utils/shiftFilters';

interface ShiftDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedShift: Shift | null;
  title: string;
  buttons?: Array<{
    label: string;
    onClick: () => void;
    variant: 'primary' | 'secondary';
    icon?: React.ReactNode;
  }>;
  showTeacherProfile?: boolean;
  showContactInfo?: boolean;
  children?: React.ReactNode;
}

/**
 * Reusable modal for displaying shift details with optional teacher profile
 */
export const ShiftDetailsModal: React.FC<ShiftDetailsModalProps> = ({
  isOpen,
  onClose,
  selectedShift,
  title,
  buttons = [],
  showTeacherProfile = true,
  showContactInfo = false,
  children,
}) => {
  const { imageUrl: teacherImageUrl, imageLoading: teacherImageLoading } = useImage(
    selectedShift?.user_teacher?.avatar_url,
  );

  // Extract substitute name logic into reusable function
  const getSubstituteName = () => {
    if (!selectedShift?.shift_reactions) return undefined;

    // First try to find by teacher_id, then fallback to any approved reaction with substitute name
    const exactMatch = selectedShift.shift_reactions.find(
      (reaction) => reaction.teacher_id === selectedShift.teacher_id,
    )?.second_substitute_name;

    const approvedMatch = selectedShift.shift_reactions.find(
      (reaction) => reaction.status === 'approved' && reaction.second_substitute_name,
    )?.second_substitute_name;

    return exactMatch || approvedMatch;
  };

  if (!selectedShift) return null;

  return (
    <Modal title={title} buttons={buttons} isOpen={isOpen} onClose={onClose}>
      {/* Shift Information */}
      <SchoolShiftInfoCard
        shiftId={selectedShift.id}
        group={selectedShift.group}
        date={selectedShift.shift_date}
        timeFrom={selectedShift.time_from}
        timeUntil={selectedShift.time_till}
        teacherName={
          selectedShift.teacher_id
            ? `${selectedShift.user_teacher?.first_name} ${selectedShift.user_teacher?.second_name}`
            : undefined
        }
        numberOfReactions={ShiftFilters.countReactionsWithStatus(
          selectedShift,
          ShiftReactionStatus.APPLIED,
        )}
      />

      {/* Teacher Profile - only show if teacher exists and showTeacherProfile is true */}
      {showTeacherProfile &&
        selectedShift.user_teacher &&
        (showContactInfo ? (
          <ProfileInfoCard
            firstName={selectedShift.user_teacher.first_name}
            secondName={selectedShift.user_teacher.second_name}
            imageUrl={teacherImageUrl}
            imageLoading={teacherImageLoading}
            email={selectedShift.user_teacher.email}
            phoneNumber={selectedShift.user_teacher.phone_number}
            address={selectedShift.user_teacher.address}
            whatsappEnabled={selectedShift.user_teacher.whatsapp_enabled}
            showContactInfo={true}
            substituteName={getSubstituteName()}
            centered
          />
        ) : (
          <ProfileInfoCard
            firstName={selectedShift.user_teacher.first_name}
            secondName={selectedShift.user_teacher.second_name}
            imageUrl={teacherImageUrl}
            imageLoading={teacherImageLoading}
            centered
          />
        ))}

      {/* Additional content */}
      {children}
    </Modal>
  );
};
